import { useContext, useMemo } from "react";
import { TransactionsPairForReconciliation } from "../../common/contracts/reconciliation.contract";
import { Button } from "../general/Button/Button";
import { ReconciliationContext } from "./Reconciliation.context";

interface Props {
    pair: TransactionsPairForReconciliation;
}

export const ReconciliationAction: React.FC<Props> = ({ pair }) => {
    const {
        financialTransactionsToRemoveIds,
        statementTransactionsToAddIds,
        registerFinancialTransactionToRemove,
        registerStatementTransactionToAdd,
        unregisterFinancialTransactionToRemove,
        unregisterStatementTransactionToAdd,
    } = useContext(ReconciliationContext);

    const isAccepted = useMemo(() => {
        if (pair.fromAccount) {
            return financialTransactionsToRemoveIds.includes(
                pair.fromAccount.financialTransactionId,
            );
        }
        if (pair.fromStatement) {
            return statementTransactionsToAddIds.includes(
                pair.fromStatement.statementTransactionId,
            );
        }
        return false;
    }, [financialTransactionsToRemoveIds, statementTransactionsToAddIds, pair]);

    const acceptAction = () => {
        if (pair.fromAccount) {
            registerFinancialTransactionToRemove(
                pair.fromAccount.financialTransactionId,
            );
        }
        if (pair.fromStatement) {
            registerStatementTransactionToAdd(
                pair.fromStatement.statementTransactionId,
            );
        }
    };

    const undoAction = () => {
        if (pair.fromAccount) {
            unregisterFinancialTransactionToRemove(
                pair.fromAccount.financialTransactionId,
            );
        }
        if (pair.fromStatement) {
            unregisterStatementTransactionToAdd(
                pair.fromStatement.statementTransactionId,
            );
        }
    };

    const message = useMemo(() => {
        if (pair.fromAccount && !isAccepted) {
            return "Remove additional transaction";
        }
        if (pair.fromAccount && isAccepted) {
            return "Transaction removed";
        }
        if (pair.fromStatement && !isAccepted) {
            return "Add missing transaction";
        }
        if (pair.fromStatement && isAccepted) {
            return "Transaction added";
        }
        return "";
    }, [pair, isAccepted]);

    return (
        <>
            <span>{message}</span>
            {isAccepted ? (
                <Button variant="secondary" size="sm" onClick={undoAction}>
                    Undo
                </Button>
            ) : (
                <Button variant="secondary" size="sm" onClick={acceptAction}>
                    Accept
                </Button>
            )}
        </>
    );
};
